import { DictionaryInterface } from 'titus-ts/dist/js/Common/Util/DictionaryInterface';
import BuiltInProperties from 'titus-ts/dist/js/Document/BuiltInProperties';
import { AddinApi } from './AddinApi';
import { HeaderFooterPage } from './HeaderFooterOptions';

export class WordApi extends AddinApi {
    private readonly supportedRetainedFieldCodes = new Set([
        'AUTHOR',
        'FILENAME',
        'NUMPAGES',
        'PAGE',
        'PRINTDATE',
        'SAVEDATE',
        'SUBJECT',
        'TITLE',
    ]);

    constructor() {
        super();
    }

    public getBodyAsTextAsync = (): Promise<string> => {
        return new Promise<string>((resolve, reject) => {
            Word.run(async (context) => {
                const body = context.document.body;
                context.load(body);
                await context.sync();
                if (!body) reject('word getBodyAsTextAsync failed');
                else {
                    let text = '';
                    context.load(body.paragraphs);
                    await context.sync();
                    body.paragraphs.items.forEach((item, index, arr) => {
                        text += item.text;
                        if (index !== arr.length - 1) {
                            text += '\n';
                        }
                    });
                    resolve(text);
                }
            });
        });
    };

    public getHeaderContent = (): Promise<string> => {
        return new Promise((resolve) => {
            Word.run(async (context) => {
                const firstSection = context.document.sections.getFirstOrNullObject();
                if (firstSection) {
                    const header = firstSection.getHeader('Primary');
                    context.load(header);
                    await context.sync();
                    resolve(header.text);
                }
                resolve('');
            });
        });
    };

    public getFooterContent = (): Promise<string> => {
        return new Promise((resolve) => {
            Word.run(async (context) => {
                const firstSection = context.document.sections.getFirstOrNullObject();
                if (firstSection) {
                    const footer = firstSection.getFooter('Primary');
                    context.load(footer);
                    await context.sync();
                    resolve(footer.text);
                }
                resolve('');
            });
        });
    };

    public getProperties = (): Promise<BuiltInProperties> => {
        return new Promise<BuiltInProperties>((resolve, reject) => {
            Word.run(async (context) => {
                const properties = context.document.properties;
                context.load(properties);
                await context.sync();
                if (!properties) reject('Word properties is null');
                else {
                    const docProperties: BuiltInProperties = {
                        Author: properties.author,
                        Category: properties.category,
                        Comments: properties.comments,
                        CreationDate: properties.creationDate?.toISOString(),
                        Keywords: properties.keywords,
                        LastAuthor: properties.lastAuthor,
                        LastPrintDate: properties.lastPrintDate?.toISOString(),
                        LastSaveTime: properties.lastSaveTime?.toISOString(),
                        RevisionNumber: properties.revisionNumber,
                        Subject: properties.subject,
                        Template: properties.template,
                        Title: properties.title,
                        NumberOfPages: '',
                    };
                    resolve(docProperties);
                }
            });
        });
    };

    public getCustomProperties = (
        requestedCustomPropertiesNames: string[],
    ): Promise<DictionaryInterface<string | string[]>> => {
        return new Promise<DictionaryInterface<string | string[]>>((resolve, reject) => {
            Word.run(async (context) => {
                const selectedCustomProperties: DictionaryInterface<string | string[]> = {};
                let successful = true;
                let errorCount = 0;
                do {
                    try {
                        const customProperties = context.document.properties.customProperties;
                        context.load(customProperties);
                        await context.sync();
                        if (!customProperties.items) reject('Word get Custom Properties failed');
                        else {
                            customProperties.items.forEach((item) => {
                                if (requestedCustomPropertiesNames.includes(item.key)) {
                                    selectedCustomProperties[item.key] = item.value.split(';');
                                }
                            });
                        }
                        successful = true;
                    } catch {
                        errorCount += 1;
                        await this.sleep(1000);
                        if (errorCount === 5) reject(new Error('customProperties could not be loaded'));
                        successful = false;
                    }
                } while (!successful);
                resolve(selectedCustomProperties);
            });
        });
    };

    public getAllCustomProperties = (): Promise<DictionaryInterface<string | string[]>> => {
        return new Promise<DictionaryInterface<string | string[]>>((resolve, reject) => {
            Word.run(async (context) => {
                let successful = true;
                let errorCount = 0;
                do {
                    try {
                        const customProperties = context.document.properties.customProperties;
                        context.load(customProperties);
                        await context.sync();
                        if (!customProperties.items) reject('Word get Custom Properties failed');
                        else {
                            const allProps: DictionaryInterface<string | string[]> = Object.fromEntries(
                                Array.from(customProperties.items, ({ key, value }) => [key, value]),
                            );

                            resolve(allProps);
                        }
                        successful = true;
                    } catch {
                        errorCount += 1;
                        await this.sleep(1000);
                        if (errorCount === 5) reject(new Error('customProperties could not be loaded'));
                        successful = false;
                    }
                } while (!successful);
            });
        });
    };

    public setCustomProperties = (
        selectedCustomProperties: DictionaryInterface<string | string[]>,
        associatedCustomPropertiesNames: string[],
    ): Promise<void> => {
        return new Promise<void>((resolve) => {
            Word.run(async (context) => {
                const fields = Object.getOwnPropertyNames(selectedCustomProperties);
                const customProperties = context.document.properties.customProperties;
                context.load(customProperties);
                for (let i = 0; i < associatedCustomPropertiesNames.length; i++) {
                    customProperties.getItemOrNullObject(associatedCustomPropertiesNames[i])?.delete();
                }
                for (let i = 0; i < fields.length; i++) {
                    const valueString =
                        typeof selectedCustomProperties[fields[i]] == 'string'
                            ? selectedCustomProperties[fields[i]]
                            : (selectedCustomProperties[fields[i]] as string[]).join(';');
                    context.document.properties.customProperties.add(fields[i], valueString);
                }
                await context.sync();
                resolve();
            });
        });
    };
    public insertPortionMark = async (portionMarkStartText: string, portionMarkEndText: string): Promise<void> => {
        // --- WORD: insert at beginning/end of the paragraph containing the selection ---

        const START_TAG = 'portionMark:start';
        const END_TAG = 'portionMark:end';
        try {
            await Word.run(async (context) => {
                const selection = context.document.getSelection();

                // Get all paragraphs that overlap with the selection
                const paragraphs = selection.paragraphs;
                paragraphs.load('items');
                await context.sync();

                // Insert start and end marks in each paragraph
                for (const paragraph of paragraphs.items) {
                    const startRange = paragraph.getRange(Word.RangeLocation.start);
                    const endRange = paragraph.getRange(Word.RangeLocation.end);

                    // Look for existing controls with our tags inside this paragraph
                    const paraRange = paragraph.getRange();
                    const existingEndCCs = paraRange.contentControls.getByTag(END_TAG);
                    const existingStartCCs = paraRange.contentControls.getByTag(START_TAG);
                    existingEndCCs.load('items');
                    existingStartCCs.load('items');
                    await context.sync();

                    if (existingEndCCs.items.length > 0) {
                        const cc = existingEndCCs.items[0];
                        // if the portion marking is empty delete the content control otherwise replace the contents with the new marking
                        if (!portionMarkEndText || portionMarkEndText.length == 0) {
                            cc.delete(false);
                        } else {
                            cc.getRange().insertHtml(portionMarkEndText, Word.InsertLocation.replace);
                        }
                    } else if (portionMarkEndText && portionMarkEndText.length > 0) {
                        // Create a new (hidden) content control at the paragraph start and insert HTML
                        const cc = endRange.insertContentControl();
                        cc.tag = END_TAG;
                        cc.title = 'Portion Mark End';
                        cc.appearance = Word.ContentControlAppearance.hidden;
                        cc.removeWhenEdited = false;
                        cc.getRange().insertHtml(portionMarkEndText, Word.InsertLocation.replace);
                    }

                    // START marker
                    if (existingStartCCs.items.length > 0) {
                        const cc = existingStartCCs.items[0];
                        if (!portionMarkStartText || portionMarkStartText.length == 0) {
                            cc.delete(false);
                        } else {
                            cc.getRange().insertHtml(portionMarkStartText, Word.InsertLocation.replace);
                        }
                    } else if (portionMarkStartText && portionMarkStartText.length > 0) {
                        const cc = startRange.insertContentControl();
                        cc.tag = START_TAG;
                        cc.title = 'Portion Mark Start';
                        cc.appearance = Word.ContentControlAppearance.hidden; // Hide brackets
                        cc.removeWhenEdited = false;
                        cc.getRange().insertHtml(portionMarkStartText, Word.InsertLocation.replace);
                    }
                }

                await context.sync();
                console.info(`Inserted portion marks at the start and end of ${paragraphs.items.length} paragraph(s).`);
            });
        } catch (error) {
            console.error('Word API error:', error);
        }
    };

    private sleep = (milliseconds: number): Promise<void> => {
        return new Promise((resolve) => setTimeout(resolve, milliseconds));
    };

    public getPlatform = (): string => {
        const platformType = this.getPlatformType();
        let platform: string;
        switch (platformType) {
            case 'Android':
                platform = 'WordAndroid';
                break;
            case 'iOS':
            case 'Mac':
                platform = 'WordIOS';
                break;
            case 'OfficeOnline':
                platform = 'WordWebApp';
                break;
            default:
                platform = 'Word';
                break;
        }
        return platform;
    };

    public parseSections = (sections: string): number[] => {
        if (!isNaN(Number(sections))) {
            // if the input is a number, shorcut and return that wrapped in an array
            return [Number(sections)];
        }

        const result: number[] = [];

        // split the input string by commas to get individual sections or section ranges
        sections.split(',').forEach((section) => {
            if (section.includes('-')) {
                // if the section contains a dash, assume it is a range and extract the start and end numbers
                const [start, end] = section.split('-');
                const startNumber = Number(start);
                const endNumber = Number(end);

                if (
                    start.length == 0 ||
                    end.length == 0 ||
                    isNaN(startNumber) ||
                    isNaN(endNumber) ||
                    endNumber < startNumber
                ) {
                    // the range is invalid so return empty array
                    return [];
                }

                for (let i = startNumber; i <= endNumber; i++) {
                    result.push(i);
                }
            } else {
                // if the section does not contain a dash, assume it is a single number and add it directly to the result array
                const sectionNumber = Number(section);
                if (isNaN(sectionNumber)) {
                    // the section number is invalid so return empty array
                    return [];
                }
                result.push(Number(section));
            }
        });

        // filter out any duplicate entries then sort the array smallest to largest and return the result array containing all the section numbers
        return result
            .filter((number, index, array) => {
                return array.indexOf(number) === index;
            })
            .sort((a, b) => a - b);
    };

    public setHeaderAndFooterAsync = async (
        location: 'header' | 'footer',
        content: string,
        position: 'start' | 'end',
        clearExisting: boolean,
        sections: 'all' | string,
        bookmarkPrefix: string,
        pages: HeaderFooterPage[] = [],
        retainFields: string[] = [],
    ): Promise<void> => {
        return new Promise((resolve) => {
            Word.run(async (context) => {
                // Determine which section numbers should contain a marking
                const sectionsArr: number[] = this.parseSections(sections);
                const selectedHeaderFooterTypes = this.getSelectedHeaderFooterTypes(pages);

                let headerFooterInsertLocation: Word.InsertLocation;
                if (position == 'start') headerFooterInsertLocation = Word.InsertLocation.start;
                else headerFooterInsertLocation = Word.InsertLocation.end;

                // Get all the sections in the document
                const documentSections = context.document.sections;
                context.document.sections.load('items');

                await context.sync();

                // Loop through each section
                for (let sectionIndex = 0; sectionIndex < documentSections.items.length; sectionIndex++) {
                    const section = documentSections.items[sectionIndex];
                    const sectionNumber = sectionIndex + 1;

                    // loop through each header/footer type
                    for (const headerFooterType of Object.values(Word.HeaderFooterType)) {
                        const shouldApply =
                            (sections.toLowerCase() == 'all' || sectionsArr.includes(sectionNumber)) &&
                            selectedHeaderFooterTypes.includes(headerFooterType);
                        if (!shouldApply) continue;

                        // Generate a bookmark name consistent with other platforms to ensure interoperability.
                        const bookmarkName =
                            bookmarkPrefix +
                            sectionNumber +
                            (location == 'header' ? 'Header' : 'Footer') +
                            headerFooterType;

                        // Advanced actions may use a configurable prefix. Also remove the historical
                        // hard-coded Titus bookmark so an existing document is migrated without duplication.
                        const legacyBookmarkName =
                            'Titus' + sectionNumber + (location == 'header' ? 'Header' : 'Footer') + headerFooterType;
                        const bookmarkNames = Array.from(new Set([bookmarkName, legacyBookmarkName]));

                        for (const existingBookmarkName of bookmarkNames) {
                            let markingRange: Word.Range | null;
                            try {
                                markingRange = context.document.getBookmarkRange(existingBookmarkName);
                                await context.sync();
                            } catch (exception) {
                                markingRange = null;
                            }

                            if (markingRange) {
                                // Remove the previous DCS marking before taking a retained-field snapshot,
                                // otherwise its text is restored alongside the field and then duplicated.
                                markingRange.delete();
                                context.document.deleteBookmark(existingBookmarkName);
                                await context.sync();
                            }
                        }

                        let headerFooterBody: Word.Body;
                        if (location == 'header') {
                            headerFooterBody = section.getHeader(headerFooterType);
                        } else {
                            headerFooterBody = section.getFooter(headerFooterType);
                        }

                        const reusableParagraph = clearExisting
                            ? await this.clearHeaderFooterRetainingFields(
                                  context,
                                  headerFooterBody,
                                  retainFields,
                                  headerFooterInsertLocation,
                              )
                            : undefined;

                        // Insert the new marking after any retained content has been restored.
                        const paragraph =
                            reusableParagraph || headerFooterBody.insertParagraph('', headerFooterInsertLocation);
                        const range = paragraph.insertHtml(content, 'Replace');
                        range.insertBookmark(bookmarkName);

                        await context.sync();
                    }
                }
                resolve();
            });
        });
    };

    private getSelectedHeaderFooterTypes = (pages: HeaderFooterPage[]): Word.HeaderFooterType[] => {
        if (pages.length === 0) return Object.values(Word.HeaderFooterType);

        const result: Word.HeaderFooterType[] = [];
        if (pages.includes('First')) result.push(Word.HeaderFooterType.firstPage);
        if (pages.includes('Odd')) result.push(Word.HeaderFooterType.primary);
        if (pages.includes('Even')) result.push(Word.HeaderFooterType.evenPages);
        return result;
    };

    private clearHeaderFooterRetainingFields = async (
        context: Word.RequestContext,
        headerFooterBody: Word.Body,
        retainFields: string[],
        insertLocation: Word.InsertLocation,
    ): Promise<Word.Paragraph | undefined> => {
        if (this.getPlatform() === 'WordWebApp') {
            await this.removeWordOnlineMarkingsUnlessRetainedFieldExists(context, headerFooterBody, retainFields);
            return undefined;
        }

        if (retainFields.length === 0) {
            headerFooterBody.clear();
            return undefined;
        }

        // Field inspection requires WordApi 1.4. Avoid destroying fields when
        // an older host cannot determine which content must be retained.
        if (!Office.context.requirements.isSetSupported('WordApi', '1.4')) {
            console.warn('RemoveMarkings was skipped because RetainFields requires WordApi 1.4.');
            return undefined;
        }

        const retainedFieldCodes = this.getRetainedFieldCodes(retainFields);
        const paragraphs = headerFooterBody.paragraphs;
        paragraphs.load('items');
        await context.sync();

        for (const paragraph of paragraphs.items) {
            paragraph.fields.load('items');
        }
        await context.sync();

        for (const paragraph of paragraphs.items) {
            paragraph.fields.load('code');
        }
        await context.sync();

        const retainedParagraphs = paragraphs.items.filter(
            (paragraph) =>
                paragraph.fields.items.length > 0 &&
                paragraph.fields.items.every((field) => {
                    const fieldCode = field.code.trim().split(/\s+/)[0].toUpperCase();
                    return retainedFieldCodes.has(fieldCode);
                }),
        );

        const retainedParagraphSet = new Set(retainedParagraphs);
        const clearedParagraphs = paragraphs.items.filter((paragraph) => !retainedParagraphSet.has(paragraph));
        for (const paragraph of clearedParagraphs) {
            paragraph.getRange('Content').delete();
        }
        await context.sync();

        if (clearedParagraphs.length === 0) return undefined;
        if (retainedParagraphs.length === 0) {
            return insertLocation === Word.InsertLocation.start
                ? clearedParagraphs[0]
                : clearedParagraphs[clearedParagraphs.length - 1];
        }

        const firstRetainedIndex = paragraphs.items.indexOf(retainedParagraphs[0]);
        const lastRetainedIndex = paragraphs.items.indexOf(retainedParagraphs[retainedParagraphs.length - 1]);
        if (insertLocation === Word.InsertLocation.start) {
            return paragraphs.items.find(
                (paragraph, index) => index < firstRetainedIndex && !retainedParagraphSet.has(paragraph),
            );
        }

        return [...paragraphs.items]
            .reverse()
            .find((paragraph, reverseIndex) => {
                const index = paragraphs.items.length - reverseIndex - 1;
                return index > lastRetainedIndex && !retainedParagraphSet.has(paragraph);
            });
    };

    private getRetainedFieldCodes = (retainFields: string[]): Set<string> =>
        new Set(
            retainFields
                .flatMap((value) =>
                    Array.from(value.matchAll(/\{([^}]+)\}/g), (match) => match[1].trim().toUpperCase()),
                )
                .filter((fieldCode) => this.supportedRetainedFieldCodes.has(fieldCode)),
        );

    private removeWordOnlineMarkingsUnlessRetainedFieldExists = async (
        context: Word.RequestContext,
        headerFooterBody: Word.Body,
        retainFields: string[],
    ): Promise<void> => {
        const retainedFieldCodes = this.getRetainedFieldCodes(retainFields);
        const fields = headerFooterBody.fields;
        fields.load('items');
        await context.sync();

        if (retainedFieldCodes.size > 0 && fields.items.length > 0) {
            fields.load('code');
            await context.sync();

            const containsRetainedField = fields.items.some((field) => {
                const fieldCode = field.code.trim().split(/\s+/)[0].toUpperCase();
                return retainedFieldCodes.has(fieldCode);
            });
            if (containsRetainedField) {
                console.warn(
                    'Other header/footer markings were preserved because Word Online contains a selected retained field.',
                );
                return;
            }
        }

        if (fields.items.length === 0) {
            headerFooterBody.clear();
            await context.sync();
            return;
        }

        // Word Online rejects clearing arbitrary header/footer content, but deleting the
        // bookmarked range used by DCS markings is supported. Remove only bookmarks that
        // follow the DCS header/footer naming convention and leave user bookmarks untouched.
        const bookmarkNames = headerFooterBody.getRange('Whole').getBookmarks(true, false);
        await context.sync();

        const markingBookmarkPattern = /\d+(Header|Footer)(Primary|FirstPage|EvenPages)$/;
        for (const bookmarkName of bookmarkNames.value.filter((name) => markingBookmarkPattern.test(name))) {
            let markingRange: Word.Range | null;
            try {
                markingRange = context.document.getBookmarkRange(bookmarkName);
                await context.sync();
            } catch (exception) {
                markingRange = null;
            }

            if (markingRange) {
                markingRange.delete();
                context.document.deleteBookmark(bookmarkName);
                await context.sync();
            }
        }
    };

}
